import {
  Activity,
  BookOpen,
  Cloud,
  ExternalLink,
  Github,
  Globe,
  Globe2,
  KeyRound,
  Link2,
  ShieldAlert,
  type LucideIcon,
} from 'lucide-react';
import type { QuickLink } from '../../types';

// Explicit map (rather than importing the whole lucide-react namespace) so unused
// icons stay tree-shaken out of the production bundle. Add an entry here whenever
// a new icon name is referenced from backend/src/config/quickLinks.ts.
const ICON_MAP: Record<string, LucideIcon> = {
  Activity,
  BookOpen,
  Cloud,
  Github,
  Globe,
  Globe2,
  KeyRound,
  ShieldAlert,
};

function resolveIcon(name: string): LucideIcon {
  return ICON_MAP[name] ?? Link2;
}

export function QuickLinksPanel({ links }: { links: QuickLink[] }) {
  const grouped = links.reduce<Record<string, QuickLink[]>>((acc, link) => {
    (acc[link.category] ??= []).push(link);
    return acc;
  }, {});

  return (
    <div className="space-y-5">
      {Object.entries(grouped).map(([category, items]) => (
        <div key={category}>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{category}</p>
          <div className="space-y-1.5">
            {items.map((link) => {
              const Icon = resolveIcon(link.icon);
              return (
                <a
                  key={link.id}
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex items-start gap-3 rounded-lg p-2 -mx-2 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/40"
                >
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                    <Icon size={15} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-1.5 text-sm font-medium text-slate-800 dark:text-slate-100">
                      {link.label}
                      <ExternalLink size={11} className="opacity-0 transition-opacity group-hover:opacity-60" />
                    </p>
                    <p className="text-xs text-slate-400">{link.description}</p>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
